import {
  useQuery,
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/vue-query'
import type { PaginationState } from '@tanstack/vue-table'
import type {
  ResponseByPath,
  QueryParamsByPath,
  BodyByPath,
} from '@@/api/types.js'
import type { UserWorkRole } from '@/models/users.js'
import type { Department } from '@/composables/departments.js'
import { useApi } from '@/composables/useApi.js'
import {
  buildIncludedResourceMap,
  createRelationshipBuilder,
  isResourceIdentifier,
  many,
  toPaginationQueryParams,
} from '@/utils/jsonapi.js'

const LOCUM_USERS_URL = '/{provet_id}/api/locum_users'
const LOCUM_USER_URL = '/{provet_id}/api/locum_users/{id}'
const LOCUM_ASSIGNMENTS_URL =
  '/{provet_id}/api/locum_users/{parent_lookup_user}/locum_assignments'
const LOCUM_ASSIGNMENT_END_URL =
  '/{provet_id}/api/locum_users/{parent_lookup_user}/locum_assignments/{id}'

export type LocumUserResponse = ResponseByPath<'get', typeof LOCUM_USER_URL>

export type LocumUsersListResponse = ResponseByPath<
  'get',
  typeof LOCUM_USERS_URL
>
export type LocumUsersListQueryParams = QueryParamsByPath<
  'get',
  typeof LOCUM_USERS_URL
>
export type LocumAssignmentsResponse = ResponseByPath<
  'get',
  typeof LOCUM_ASSIGNMENTS_URL
>
export type LocumAssignmentBody = BodyByPath<
  'post',
  typeof LOCUM_ASSIGNMENTS_URL
>
export type LocumAssignmentsQueryParams = QueryParamsByPath<
  'get',
  typeof LOCUM_ASSIGNMENTS_URL
>
export type LocumAssignmentEndResponse = ResponseByPath<
  'delete',
  typeof LOCUM_ASSIGNMENT_END_URL
>

export type DepartmentWithLocumData = Department & {
  attributes?: Department['attributes'] & {
    current_locum_start_date?: string
    current_locum_end_date?: string
    street_address?: string
    street_address_2?: string
  }
}

export function useLocumUsersListQuery(
  pagination: MaybeRef<PaginationState>,
  filters?: {
    search: MaybeRef<string>
    has_assignments?: MaybeRef<boolean>
    has_assignment_with_status: MaybeRef<
      ('active' | 'upcoming' | 'past' | 'cancelled')[]
    >
    permission_group_id: MaybeRef<string[]>
    work_role?: MaybeRef<(UserWorkRole | null)[]>
    is_active?: MaybeRef<boolean>
  },
) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed(() => ({
    ...toPaginationQueryParams(pagination),
    'filter[search]': unref(filters?.search) || undefined,
    'filter[has_assignments]': unref(filters?.has_assignments) ?? undefined,
    'filter[has_assignment_with_status]':
      unref(filters?.has_assignment_with_status) || undefined,
    'filter[permission_group_id]':
      unref(filters?.permission_group_id) || undefined,
    'filter[work_role]': unref(filters?.work_role) || undefined,
    'filter[is_active]':
      filters?.is_active !== undefined ? unref(filters.is_active) : true,
    include: ['earliest_assignment'],
  }))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: computed(() => ['locum-users-list', queryParams.value]),
    queryFn: async () => {
      return await api((client) =>
        client.GET(LOCUM_USERS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
            },
            query: queryParams.value,
          },
        }),
      )
    },
    select: (response): LocumUsersListResponse => {
      const included = buildIncludedResourceMap(response, ['LocumAssignment'])

      return {
        ...response,
        data: response.data.map((item) => {
          const earliestAssignmentData =
            item.relationships?.earliest_assignment?.data
          return {
            ...item,
            relationships: {
              ...(item.relationships ?? {}),
              earliest_assignment: {
                data: isResourceIdentifier(earliestAssignmentData)
                  ? (included.LocumAssignment?.[earliestAssignmentData.id] ??
                    earliestAssignmentData)
                  : earliestAssignmentData,
              },
            },
          }
        }),
      }
    },
  })

  return reactive(query)
}

export function useLocumAssignmentsQuery(
  userId: MaybeRef<string>,
  departmentId: MaybeRef<string | undefined>,
  enabled: MaybeRef<boolean>,
) {
  const api = useApi()
  const route = useRoute()

  const isEnabled = computed(() => unref(enabled) && Boolean(unref(userId)))

  const query = useQuery({
    enabled: isEnabled,
    queryKey: computed(() => [
      'locumAssignments',
      unref(userId),
      unref(departmentId),
    ]),
    queryFn: async () => {
      const currentUserId = unref(userId)

      if (!currentUserId) {
        throw new Error('User ID is required')
      }

      const currentDepartmentId = unref(departmentId)
      const depNum = Number(currentDepartmentId)

      return await api((client) =>
        client.GET(LOCUM_ASSIGNMENTS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: currentUserId,
            },
            query: {
              'filter[department_id]': Number.isFinite(depNum)
                ? depNum
                : undefined,
            },
          },
        }),
      )
    },
  })

  return reactive(query)
}

export function useLocumAssignmentCreateMutation() {
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: async (data: {
      userId: MaybeRef<string>
      dates: LocumAssignmentBody['data']['attributes']
    }) => {
      return await api((client) =>
        client.POST(LOCUM_ASSIGNMENTS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: unref(data.userId),
            },
          },
          body: {
            data: {
              type: 'LocumAssignment',
              attributes: data.dates,
            },
          },
        }),
      )
    },
  })
}

export function useLocumUserQuery(id: MaybeRef<string>) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    enabled: computed(() => Boolean(unref(id))),
    queryKey: ['locumUser', unref(id)],
    queryFn: async () => {
      return await api((client) =>
        client.GET(LOCUM_USER_URL, {
          params: {
            path: { provet_id: route.params.provetId, id: unref(id) },
            query: {
              include: [
                'available_departments_permanent',
                'available_departments_non_permanent',
              ],
            },
          },
        }),
      )
    },
    select: (response) => {
      const included = buildIncludedResourceMap(response, ['Department'])
      const relationships = createRelationshipBuilder({
        available_departments_permanent: many('Department'),
        available_departments_non_permanent: many('Department'),
      })(response.data as object, included)

      return {
        ...response.data,
        relationships: relationships as {
          available_departments_permanent: DepartmentWithLocumData[]
          available_departments_non_permanent: DepartmentWithLocumData[]
        },
      }
    },
  })

  return reactive(query)
}

export function useLocumAssignmentUpdateMutation() {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: async (data: {
      id: string
      userId: string
      dates: LocumAssignmentBody['data']['attributes']
    }) => {
      return await api((client) =>
        client.PATCH(LOCUM_ASSIGNMENT_END_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: data.userId,
              id: data.id,
            },
          },
          body: {
            data: {
              type: 'LocumAssignment',
              id: data.id,
              attributes: data.dates,
            },
          },
        }),
      )
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['locumAssignments', variables.userId],
      })
      await queryClient.invalidateQueries({ queryKey: ['locum-users-list'] })
    },
  })
}

export function useLocumAssignmentEndMutation() {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: async (data: { id: string; userId: string }) => {
      return await api((client) =>
        client.DELETE(LOCUM_ASSIGNMENT_END_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: data.userId,
              id: data.id,
            },
          },
        }),
      )
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['locumAssignments', variables.userId],
      })
      await queryClient.invalidateQueries({ queryKey: ['locum-users-list'] })
      await queryClient.invalidateQueries({
        queryKey: ['clinic-staff-list', route.params.provetId],
      })
    },
  })
}
